'use client'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useMemo } from 'react'

const width = 1920
const height = 1192

interface MapLayer {
    image: string
    section?: string
    tables?: (number | string)[]
    level: number
    alwaysVisible?: boolean
    bg?: boolean
    noBlur?: boolean
    selection?: boolean // Indica se il livello è solo un perimetro di selezione
}
const mapLayers: MapLayer[] = [
    {
        image: '/map/d20.webp',
        level: 100,
        section: '20',
        tables: [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24],
    },
    {
        image: '/map/p-d20.webp',
        level: 100,
        section: '20',
        selection: true,
    },
    {
        image: '/map/d12.webp',
        level: 90,
        section: '12',
        tables: [
            29, 31, 33, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 50,
        ],
    },
    {
        image: '/map/p-d12.webp',
        level: 90,
        section: '12',
        selection: true,
    },
    {
        image: '/map/d10.webp',
        level: 80,
        section: '10',
        tables: [
            51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67,
            68, 69, 70, 71, 72, 73, 75, 77,
        ],
    },
    {
        image: '/map/p-d10.webp',
        level: 80,
        section: '10',
        selection: true,
    },
    {
        image: '/map/d6.webp',
        level: 70,
        section: '6',
        tables: [4, 5, 6, 7, 8, '8b', '5b', '4b', 49],
    },
    {
        image: '/map/p-d6.webp',
        level: 70,
        section: '6',
        selection: true,
    },
    {
        image: '/map/d4.webp',
        level: 60,
        section: '4',
        tables: [
            25,
            26,
            '27a',
            '27b',
            '28a',
            '28b',
            30,
            32,
            34,
            74,
            79,
            81,
            83,
            85,
        ],
    },
    {
        image: '/map/p-d4.webp',
        level: 60,
        section: '4',
        selection: true,
    },
    {
        image: '/map/altro.webp',
        level: 50,
        section: 'common',
        tables: [1, 2, 3, 9, -1],
    },
    { image: '/map/bg.webp', level: 10, alwaysVisible: true, bg: true },
]

export default function Map({
    highlightTable = '',
    sizeMlt = 1,
}: {
    highlightTable?: string
    sizeMlt?: number
}) {
    const params = useSearchParams()
    const selectedSection = useMemo(() => {
        return mapLayers
        .filter((x) => x.tables?.length)
        .filter((x) => {
            return x.tables?.map((x) => x.toString()).includes(highlightTable.toString()) || x.tables?.map((x) => x.toString()).includes(params.get("table")??'')
        })?.[0]?.section
    }, [highlightTable, params])

    const table = selectedSection ? highlightTable ? highlightTable : params.get("table") : ''
    
    const somethingSelected = table && selectedSection
    return (
        <>
            <div
                className="flex w-fit"
                style={{
                    border: 'solid 1px black',
                    marginBottom: '25px',
                    maxWidth: '96vw',
                    overflowX: 'auto',
                    position: 'relative',
                }}
            >
                {mapLayers.map((layer, i) => {
                    const activeSection =
                        table &&
                        selectedSection === layer.section

                    const highlightThis = activeSection && !layer.selection // layers.selection means it's the area perimeter layer (below the section layer)

                    const blurMe =
                        !highlightThis &&
                        somethingSelected &&
                        !layer.alwaysVisible &&
                        !layer.noBlur &&
                        !layer.selection

                    const obscureMe = layer.bg && somethingSelected // Get ground and other images darker when something is selected
                    return (
                        <Image
                            key={layer.image}
                            alt={`map-lev-${i}`}
                            src={layer.image}
                            width={width * sizeMlt}
                            height={height * sizeMlt}
                            unoptimized
                            style={{
                                display: layer.selection
                                    ? activeSection
                                        ? 'block'
                                        : 'none'
                                    : 'block',
                                filter: blurMe
                                    ? 'blur(2px) brightness(80%)'
                                    : obscureMe
                                      ? blurMe
                                          ? 'brightness(60%) blur(2px)'
                                          : 'brightness(60%)'
                                      : '',
                                height: height * sizeMlt,
                                maxWidth: width * sizeMlt,
                                userSelect: 'none',
                                zIndex:
                                    layer.level +
                                    (highlightThis ? 100 : 0) -
                                    (layer.selection ? 1 : 0),
                                position: 'absolute',
                                top: 0,
                            }}
                        />
                    )
                })}
                <Image
                    alt="map-bg"
                    src="/map/place.webp"
                    width={width * sizeMlt}
                    height={height * sizeMlt}
                    unoptimized
                    style={{
                        userSelect: 'none',
                        visibility: 'hidden',
                        height: height * sizeMlt,
                        maxWidth: width * sizeMlt,
                    }}
                />
            </div>
        </>
    )
}
